import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import * as sup from "superstruct";

import { AnonymousLayout } from "@/layouts/AnonymousLayout/AnonymousLayout";
import { Form } from "@/modules/form/components/Form/Form";
import { PasswordInput } from "@/modules/form/components/PasswordInput/PasswordInput";
import { TextInput } from "@/modules/form/components/TextInput/TextInput";
import { emailPattern, notEmpty } from "@/modules/form/utils/structs";
import { AnonymousPageBox } from "@/modules/theme/components/AnonymousPageBox";
import { Button } from "@/modules/theme/components/Button/Button";
import { ButtonWithArrow } from "@/modules/theme/components/ButtonWithArrow/ButtonWithArrow";
import { PageMeta } from "@/modules/theme/components/PageMeta";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";
import { recompose } from "@/utils/structs/recompose";

import { ResetPasswordDialog } from "./components/ResetPasswordDialog";
import { createSignInPageStyles } from "./helpers/createSignInPageStyles";
import { useSignInMutation } from "./hooks/useSignInMutation";
import { ReactComponent as PersonIcon } from "./icons/person.svg";

const translationNamespace = "pages/SignInPage";

const signInStruct = sup.object({
  email: recompose(sup.string(), notEmpty, emailPattern),
  password: recompose(sup.string(), notEmpty),
});

export function SignInPage() {
  const { t } = useTranslation(translationNamespace);
  const theme = useTheme();
  const classes = createSignInPageStyles(theme);

  const signInMutation = useSignInMutation();
  const [isResetPasswordDialogOpen, setIsOpenResetDialogOpen] = useState(false);

  return (
    <AnonymousLayout>
      <PageMeta title={t("Login")} />
      <AnonymousPageBox className={classes.pageBoxWrapper}>
        <div className={classes.welcomeWrapper}>
          <Typography variant="headingLarge" className={classes.welcomeText}>
            <Trans t={t} i18nKey="Welcome <br /> to login!" />
          </Typography>
        </div>
        <div className={classes.formWrapper}>
          <Form
            defaultValues={{
              email: "",
              password: "",
            }}
            struct={signInStruct}
            onValidSubmit={signInMutation.mutate}
          >
            {({ register }) => {
              return (
                <>
                  <TextInput
                    label={t("Email")}
                    type="email"
                    placeholder={t("example@example.com")}
                    {...register("email")}
                    startAdornment={<PersonIcon />}
                    errorMessages={{
                      "string.notEmpty": t("Email is required"),
                      "string.emailPattern": t("Invalid email address"),
                    }}
                  />
                  <PasswordInput
                    label={t("Password")}
                    placeholder={t("Password")}
                    {...register("password")}
                    errorMessages={{
                      "string.notEmpty": t("Password is required"),
                    }}
                  />
                  <div className={classes.buttonsWrapper}>
                    <Button
                      variant="text"
                      onClick={() => setIsOpenResetDialogOpen(true)}
                      className={classes.forgotPassword}
                    >
                      {t("Forgot password?")}
                    </Button>
                    <ButtonWithArrow
                      className={classes.submitButton}
                      isLoading={signInMutation.status === "loading"}
                      isDisabled={signInMutation.status === "loading"}
                      withShadow
                      type="submit"
                    >
                      {t("Login")}
                    </ButtonWithArrow>
                  </div>
                </>
              );
            }}
          </Form>
        </div>
      </AnonymousPageBox>
      <ResetPasswordDialog
        isOpen={isResetPasswordDialogOpen}
        onClose={() => setIsOpenResetDialogOpen(false)}
      />
    </AnonymousLayout>
  );
}
